// AI-GENERATED
// 365 Copilot | GPT-5 Chat model (GPT-5.2)
// Tarkistanut Oskari Rintamäki
// Date: 2026-05-18

import React, { useMemo, useState, useCallback } from 'react';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { setIsCustomFilterOpen } from '../../../state/slices/uiSlice';
import styled from 'styled-components';

import LayerGroup from './LayerGroup';
import Layers from '../../layer/Layers';

import { motion } from 'motion/react';
import strings from '../../../translations';
import PillButton from '../../../utils/components/PillButton';
import { getDescTagContent } from '../../../utils/gfiUtil';
import { ReportGroup } from './reports/ReportGroup';

const masterHeaderIconVariants = {
  open: { rotate: 180 },
  closed: { rotate: 0 }
};

const listVariants = {
  visible: { height: 'auto', opacity: 1 },
  hidden: { height: 0, opacity: 0 }
};

const StyledLayerList = styled.div``;

const StyledLayerGroups = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.mainWhite};
  margin: ${(props) => props.parentId === -1 && '10px 0px 10px 0px'};
  border-radius: 2px;
  &:last-child {
    ${(props) =>
      props.parentId === -1
        ? '1px solid ' + props.theme.colors.mainColor2
        : 'none'};
  }
`;

const StyledMasterGroupHeader = styled.div`
  position: sticky;
  top: -8px;
  z-index: 1;
  min-height: 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.mainColor1};
  border-radius: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  border: none;
  width: 100%;
  text-align: left;
  @-moz-document url-prefix() {
    position: initial;
  }
  &:focus {
    outline: 2px solid ${(p) => p.theme.colors.mainColor1Selected};
    outline-offset: 2px;
  }
`;

const StyledMasterGroupName = styled.p`
  user-select: none;
  max-width: 240px;
  color: ${(props) => props.theme.colors.mainWhite};
  margin: 0;
  padding: 9px 0px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.1s ease-in;
`;

const StyledLeftContent = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;

const StyledRightContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledMotionIconWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledMasterGroupHeaderIcon = styled.div`
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    font-size: 20px;
    color: ${(props) => props.theme.colors.mainWhite};
  }
  p {
    margin: 0;
    font-weight: bold;
    font-size: 22px;
    color: ${(props) => props.theme.colors.mainWhite};
  }
`;

const StyledMasterGroupTitleContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledSelectButton = styled.button`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  margin-right: 8px;
  border: none;
  svg {
    color: ${(props) => props.theme.colors.mainWhite};
    font-size: 19px;
    transition: all 0.3s ease-out;
  }
`;

const StyledLayerGroup = styled(motion.div)`
  margin: 0;
  padding-inline-start: 8px;
  padding: 4px 0px;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
`;

const StyledLayerGroupWrapper = styled.div``;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 5px;
  align-items: center;
  margin: 20px 0px;
  gap: 30px;
`;

const LayerList = ({ groups, layers }) => {
  const { tagLayers, tags } = useSelector((state) => state.rpc);

  const lang = strings.getLanguage();
  const defaultLang = strings.getAvailableLanguages()?.[0] ?? lang;

  // 1) Sort groups (memoized)
  const sortedGroups = useMemo(() => {
    if (!Array.isArray(groups) || groups.length === 0) return [];

    return [...groups].sort((a, b) => {
      if (b.id === 727) return -1;
      if (a.id === 727) return 1;

      const aName = a?.locale?.[lang]?.name ?? null;
      const bName = b?.locale?.[lang]?.name ?? null;

      return aName && bName
        ? aName.localeCompare(bName, lang, { sensitivity: 'base' })
        : 0;
    });
  }, [groups, lang]);

  // 2) Cache recursion: does group have children layers?
  const hasChildrenMap = useMemo(() => {
    const cache = new Map();

    const check = (group) => {
      if (!group?.id) return false;
      if (cache.has(group.id)) return cache.get(group.id);

      const hasChildrenLayers =
        (group.layers && group.layers.length > 0) ||
        (group.groups && group.groups.some(check)) ||
        false;

      cache.set(group.id, hasChildrenLayers);
      return hasChildrenLayers;
    };

    sortedGroups.forEach(check);
    return cache;
  }, [sortedGroups]);

  /**
   * 3) Identify the "reports" group and extract report links from its subgroups.
   *    Also build normal filteredGroups (excluding the reports group to avoid duplicate rendering).
   */
  const { filteredGroups, hasChildrenIds, reportsGroup, reportLinkGroups } =
    useMemo(() => {
      const fg = [];
      const childIds = [];

      // Find reports group by localized name "raportit" (case-insensitive)
      const findReportsGroup = (groupsArr) => {
        // fallback: match name in current lang (or default) equals "reports"
        return groupsArr.find((g) => {
          const name = g?.locale?.[defaultLang]?.name || '';
          return String(name).trim().toLowerCase() === 'raportit';
        });
      };

      const rg = findReportsGroup(sortedGroups) || null;

      // Extract report link items from ONE level deeper: reportsGroup.groups
      const rLinks = [];
      if (rg?.groups && Array.isArray(rg.groups)) {
        rg.groups.forEach((subgroup, index) => {
          const txt =
            (subgroup?.locale?.[lang]?.desc &&
              subgroup.locale[lang].desc.length > 0 &&
              subgroup.locale[lang].desc) ||
            (subgroup?.locale?.[defaultLang]?.desc &&
              subgroup.locale[defaultLang].desc.length > 0 &&
              subgroup.locale[defaultLang].desc) ||
            false;

          const link =
            (txt &&
              getDescTagContent(
                txt.replace(/\s+/g, ''),
                '<url>',
                '</url>'
              )[0]) ||
            null;

          if (link) {
            rLinks.push({ group: subgroup, link, index });
          }
        });
      }

      // Build normal visible groups, but skip the "raportit" group itself
      sortedGroups.forEach((group) => {
        // Skip reports group from normal rendering
        const isReports =
          String(group?.locale?.[defaultLang]?.name ?? '')
            .trim()
            .toLowerCase() === 'raportit';

        if (isReports) return;

        const hasChildren = hasChildrenMap.get(group.id) === true;
        if (hasChildren) childIds.push(group.id);

        const isVisible =
          (group.layers && group.layers.length > 0) || hasChildren;
        if (isVisible) fg.push(group);
      });

      return {
        filteredGroups: fg,
        hasChildrenIds: childIds,
        reportsGroup: rg,
        reportLinkGroups: rLinks
      };
    }, [sortedGroups, hasChildrenMap, lang, defaultLang]);

  // 4) Apply tag layer filtering without mutating props
  const derivedLayers = useMemo(() => {
    if (!Array.isArray(layers)) return [];
    if (Array.isArray(tagLayers) && tagLayers.length > 0) {
      const allowed = new Set(tagLayers);
      return layers.filter((layer) => allowed.has(layer.id));
    }
    return layers;
  }, [layers, tagLayers]);

  return (
    <>
      {Array.isArray(tagLayers) && tagLayers.length > 0 ? (
        <StyledLayerList>
          {tags.map((tag, index) => (
            <TagLayerList
              tag={tag}
              layers={derivedLayers}
              index={index}
              groups={groups}
              key={`taglayerlist-${tag}-${index}`}
            />
          ))}
        </StyledLayerList>
      ) : (
        <>
          <StyledLayerList>
            {filteredGroups.map((group) => (
              <StyledLayerGroupWrapper key={`group-sl-${group.id}`}>
                <LayerGroup
                  key={`layer-group-${group.id}`}
                  group={group}
                  layers={derivedLayers}
                  hasChildren={hasChildrenIds.includes(group.id)}
                />
              </StyledLayerGroupWrapper>
            ))}

            {/* Reports group rendered from sortedGroups["raportit"].groups (one level deeper) */}
            <ReportGroup
              reportsGroup={reportsGroup}
              reportLinkGroups={reportLinkGroups}
              lang={lang}
            />
          </StyledLayerList>
        </>
      )}
    </>
  );
};

/* --------------------------------------------------
   TagLayerList
--------------------------------------------------- */

export const TagLayerList = React.memo(
  ({ tag, layers, index, groups, customTag = [] }) => {
    const dispatch = useDispatch();
    const tagsWithLayers = useSelector((state) => state.rpc.tagsWithLayers);
    const [isOpen, setIsOpen] = useState(customTag.length > 0);

    const tagLayersResolved = useMemo(() => {
      if (customTag.length > 0) return customTag;
      return tagsWithLayers?.[tag] ?? [];
    }, [customTag, tagsWithLayers, tag]);

    const layersById = useMemo(() => {
      const map = new Map();
      (layers || []).forEach((l) => map.set(l.id, l));
      return map;
    }, [layers]);

    const filteredLayers = useMemo(() => {
      if (!Array.isArray(tagLayersResolved) || tagLayersResolved.length === 0)
        return [];
      return tagLayersResolved.map((id) => layersById.get(id)).filter(Boolean);
    }, [tagLayersResolved, layersById]);

    const toggleOpen = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    const openCustomFilter = useCallback(() => {
      dispatch(setIsCustomFilterOpen(true));
    }, [dispatch]);

    return (
      <StyledLayerGroups id={`taglayerlist-${tag}`}>
        <StyledMasterGroupHeader
          id={`smgh_${index}_`}
          aria-label={
            isOpen
              ? strings.accessibility.closeLayerGroup
              : strings.accessibility.openLayerGroup
          }
          key={`smgh_${index}_`}
          onClick={toggleOpen}
        >
          <StyledLeftContent id={`taglayerlist-${tag}left-content`}>
            <StyledMasterGroupHeaderIcon>
              <p>{tag.charAt(0).toUpperCase()}</p>
            </StyledMasterGroupHeaderIcon>
            <StyledMasterGroupTitleContent>
              <StyledMasterGroupName>
                {tag.charAt(0).toUpperCase() + tag.slice(1)}
              </StyledMasterGroupName>
            </StyledMasterGroupTitleContent>
          </StyledLeftContent>

          <StyledRightContent id={`taglayerlist-${tag}right-content`}>
            <StyledSelectButton
              aria-label={
                isOpen
                  ? strings.accessibility.closeLayerGroup
                  : strings.accessibility.openLayerGroup
              }
              type="button"
            >
              <StyledMotionIconWrapper
                initial="closed"
                animate={isOpen ? 'open' : 'closed'}
                variants={masterHeaderIconVariants}
                transition={{ duration: 0.3, type: 'tween' }}
              >
                <FontAwesomeIcon icon={faAngleDown} />
              </StyledMotionIconWrapper>
            </StyledSelectButton>
          </StyledRightContent>
        </StyledMasterGroupHeader>

        <StyledLayerGroup
          id={`taglayerlist-${tag}layer-group`}
          key={`slg_${index}_`}
          isOpen={isOpen}
          initial="hidden"
          animate={isOpen ? 'visible' : 'hidden'}
          variants={listVariants}
          transition={{ duration: 0.3, type: 'tween' }}
        >
          <Layers layers={filteredLayers} groups={groups} />

          {customTag.length > 0 && (
            <StyledButtonContainer id={`taglayerlist-${tag}button-container`}>
              <PillButton onClick={openCustomFilter}>
                {strings.layerlist.customFilterInfo.editLayers}
              </PillButton>
            </StyledButtonContainer>
          )}
        </StyledLayerGroup>
      </StyledLayerGroups>
    );
  }
);

export default React.memo(LayerList);
